import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { createContext, type FC, type ReactElement, type RefObject, useContext, useRef, useState } from 'react';

export interface IPDFContext {
  hasCtx: boolean;
  registerElement: (el: HTMLDivElement, initialMarginTop: number) => number;
  pdfRef: RefObject<HTMLDivElement>;
  handleDownloadPdf: (setLoading: (val: boolean) => void) => Promise<void>;
  setPdfName: (val: string) => void;
  pdfReadyToRender: boolean;
  setPdfReadyToRender: (val: boolean) => void;
}

export const PDFContext = createContext<IPDFContext | null>(null);

/**
 * Context to keep track of all elements, registered to context (via hooks) and paginate based on fixed page height and a group of elements, passed 1 by 1
 * @param children
 * @constructor
 */
export const PDFContextProvider: FC<{ children: ReactElement }> = ({ children }) => {
  const PAGE_HEIGHT = 2080;
  const NEW_PAGE_MARGIN_TOP = 45;
  const remainingHeightRef = useRef(PAGE_HEIGHT);
  const lastElPosition = useRef(0);
  const newPageMarginApplied = useRef(0);

  /**
   * Register element and return styles (marginTop) to apply to paginate (if needed)
   * Accepts initialMarginTop, which is passed to MUI Grid2 element and not tracked by HTML styles decomposition
   * @param el
   * @param initialMarginTop
   */
  const registerElement = (el: HTMLDivElement, initialMarginTop: number = 0): number => {
    // console.log('%c[PDF Pagination]', 'color: magenta; font-weight: bold;');
    // console.log('Element:', el);
    // console.log('ID:', el.id);

    // Margins
    const style = window.getComputedStyle(el);
    const marginTop = parseFloat(style.marginTop || '0');
    // Include initial marginTop, which is not obtained inside element's styles
    const finalMarginTop = initialMarginTop + marginTop;
    const marginBottom = parseFloat(style.marginBottom || '0');

    // Height
    const height = el.offsetHeight + finalMarginTop + marginBottom;

    // Start/End Points
    const startPoint = Math.round(el.getBoundingClientRect().top + window.scrollY);
    const endPoint = Math.round(el.getBoundingClientRect().bottom + window.scrollY);

    // Add/subtract margins and new page margin applied so far, to get total relative start/end Points
    const totalStart = startPoint - finalMarginTop + newPageMarginApplied.current;
    const totalEnd = endPoint + marginBottom + newPageMarginApplied.current;

    // console.log('%c[PDF Context] Last El Position:', 'color: yellow;', lastElPosition.current);

    // console.log('%cStart: ', 'color: red;', totalStart);
    // console.log('%cMargin Top: ', 'color: yellow;', finalMarginTop);
    // console.log('Element Start: ', Math.round(startPoint));
    // console.log('%cElement Height: ', 'color: magenta;', el.offsetHeight);
    // console.log('Element End: ', Math.round(endPoint));
    // console.log('%cMargin Bottom: ', 'color: yellow;', marginBottom);
    // console.log('%cEnd: ', 'color: red;', totalEnd);
    // console.log('%c[PDF Context] Total NewPage Margin Applied:', 'color: yellow;', newPageMarginApplied.current);

    // console.log('%cFull El Height: ', 'color: yellow;', height);

    // Calculates space between current element's start and last registered element's end position to obtain inter-element spacing, not tracked inside elements
    const spaceBetweenElements = totalStart - lastElPosition.current;

    // console.log('%c[PDF Context] Space Between Elements:', 'color: yellow;', spaceBetweenElements);

    // Element + inter-element height
    const totalHeight = height + spaceBetweenElements;

    // console.log('%c[PDF Context] Total Height:', 'color: yellow;', totalHeight);

    // console.log('%c[PDF Context] Current Remaining:', 'color: yellow;', remainingHeightRef.current);

    if (totalHeight > remainingHeightRef.current - NEW_PAGE_MARGIN_TOP) {
      // console.log('%c[PDF Context] New Page ... ', 'color: green;');

      // Calc Margin to apply -> to shift element to new page (before remaining height is updated)
      const marginToApply = remainingHeightRef.current + NEW_PAGE_MARGIN_TOP;

      // New page, subtract the height from full page height
      const toSubtract = height + NEW_PAGE_MARGIN_TOP;
      remainingHeightRef.current = PAGE_HEIGHT - toSubtract;

      // console.log('%c[PDF Context] New Remaining:', 'color: green;', remainingHeightRef.current);

      // Mark newPage Margin applied
      newPageMarginApplied.current += marginToApply;

      // Mark Last position of element (including new page Margin)
      lastElPosition.current = totalEnd + marginToApply;

      return marginToApply;
    } else {
      // Keep in same page => subtract the height from remaining page height
      const toSubtract = totalHeight;
      // console.log('%c[PDF Context] Minus:', 'color: red;', toSubtract);
      remainingHeightRef.current -= toSubtract;

      // console.log('%c[PDF Context] New Remaining:', 'color: green;', remainingHeightRef.current);

      // Mark Last position of element
      lastElPosition.current = totalEnd;

      return finalMarginTop;
    }
  };

  const pdfRef = useRef<HTMLDivElement>(null);
  const [pdfName, setPdfName] = useState<string>();
  const [pdfReadyToRender, setPdfReadyToRender] = useState(false);

  const handleDownloadPdf = async (setLoading: (val: boolean) => void) => {
    if (!pdfRef.current) return;

    // yield with double rAF
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

    setLoading(true);

    try {
      // console.log('Starting PDF generation...');

      const canvas = await html2canvas(pdfRef.current, {
        scale: 1,
        backgroundColor: '#ffffff',
        useCORS: true,
        logging: false,
        removeContainer: true,
        onclone: async (clonedDoc) => {
          // console.log('Document cloned successfully');

          const style = clonedDoc.createElement('style');
          style.textContent = `
              @font-face {
                  font-family: 'LLBrown';
                  src: url('/fonts/LLBrown.woff2') format('woff2');
                  font-weight: normal;
                  font-style: normal;
              }

              body,
              * {
                  font-family: 'LLBrown', 'LLBrown_Bold', sans-serif !important;
              }

              body {
                  width: 1470px !important;
                  max-width: 1470px !important;
              }

              * {
                  box-shadow: none !important;
                  background-blend-mode: normal !important;
              }

              @media screen and (max-width: 1920px) {
                  .MuiContainer-root {
                      max-width: none !important;
                  }
              }
            `;
          if (clonedDoc.head) {
            clonedDoc.head.appendChild(style);
          } else {
            // fallback: append to body
            clonedDoc.body.appendChild(style);
          }

          // Also copy all <style> and <link rel="stylesheet"> from original
          document.querySelectorAll('style, link[rel="stylesheet"]').forEach((node) => {
            if (clonedDoc.head) {
              clonedDoc.head.appendChild(node.cloneNode(true));
            } else {
              // fallback: append to body
              clonedDoc.body.appendChild(node.cloneNode(true));
            }
          });

          // 👇 Wait for fonts to finish loading
          await clonedDoc.fonts.ready;

          // console.log('Fonts loaded in cloned document');

          return clonedDoc;
        }
      });

      // console.log('Canvas created successfully:', canvas.width, 'x', canvas.height);

      // Generate PDF
      const imgData = canvas.toDataURL('image/jpeg', 0.8);
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'px',
        format: 'a4',
        compress: true
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgAspectRatio = canvas.height / canvas.width;
      const pdfWidth = pageWidth;
      const pdfHeight = pageWidth * imgAspectRatio;

      if (pdfHeight > pageHeight) {
        const totalPages = Math.ceil(pdfHeight / pageHeight);
        for (let i = 0; i < totalPages; i++) {
          if (i > 0) pdf.addPage();
          const yOffset = -i * pageHeight;
          pdf.addImage(imgData, 'JPEG', 0, yOffset, pdfWidth, pdfHeight);
        }
      } else {
        pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight);
      }

      pdf.save(pdfName);
      // console.log('PDF saved successfully');
    } catch (error) {
      console.error('PDF generation failed:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PDFContext.Provider
      value={{
        hasCtx: true,
        registerElement,
        pdfRef,
        handleDownloadPdf,
        setPdfName,
        pdfReadyToRender,
        setPdfReadyToRender
      }}
    >
      {children}
    </PDFContext.Provider>
  );
};

export const usePDFContext = (): IPDFContext => {
  const ctx = useContext(PDFContext);

  if (ctx) return ctx;

  // Fallback no-op context
  return {
    hasCtx: false,
    registerElement: () => 0,
    pdfRef: { current: null },
    handleDownloadPdf: async () => {},
    setPdfName: () => {},
    pdfReadyToRender: false,
    setPdfReadyToRender: () => {}
  };
};
